import React, { useContext, useMemo, useState } from "react";

import { initialState, RCAAPComplianceContext } from "./RCAAPComplianceContext";
import { IRCAAPComplianceFiltersSelected } from "pages/Corporate/RCA/RCAAPCompliance/components/filters/RCAAPComplianceFilters/RCAAPComplianceFiltersInterfaces";
import { IRCAAPComplianceContextProps } from "./RCAAPComplianceContextInterfaces";
import { UsersEntity } from "entities/Users/UsersEntity";
import { IGetUserFiltersPreferencesQuery } from "api/queries/usersAPI";
import { CorporateContext } from "contexts/CorporateContext/CorporateContext";
import { FiltersPreferencesReportsEnum } from "constants/constants";
import { useTranslation } from "react-i18next";
import { filterObjectProperties, objectComparator } from "utilities/utilities";
import { Alert, Snackbar } from "@mui/material";

export const valiRCAAPComplianceFiltersDefaultFields = [
    "periodOption",
    "typeOption",
    "meetingTypeIds",
    "totalMeetingTypes",
];

export const RCAAPComplianceContextProvider: React.FC<IRCAAPComplianceContextProps> = ({ children }) => {
    const { t } = useTranslation();
    const { currentUser } = useContext(CorporateContext);

    const [filters, setFilters] = useState<IRCAAPComplianceFiltersSelected>({
        periodOption: initialState.filters.periodOption,
        typeOption: initialState.filters.typeOption as string,
        complianceType: initialState.filters.complianceType as string,
        meetingTypeIds: initialState.filters.meetingTypeIds,
        totalMeetingTypes: initialState.filters.totalMeetingTypes,
    });
    const [userFiltersDefault, setUserFiltersDefault] = useState<IRCAAPComplianceFiltersSelected | null>(null);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    const { upsertOperation, loading: loadingUpsertDefault } = UsersEntity.UpsertUserFiltersDefaultPreference();
    const { removeOperation, loading: loadingRemoveDefault } = UsersEntity.RemoveUserFiltersDefaultPreference();

    const handleQueryCompletion = (data: IGetUserFiltersPreferencesQuery) => {
        const userFilterPreference = data.user_filter_preferences;
        if (userFilterPreference[0]) {
            const filtersDefault = JSON.parse(
                userFilterPreference[0].filter_selection,
            ) as IRCAAPComplianceFiltersSelected;
            setUserFiltersDefault(filtersDefault);
            setFilters((prev) => ({ ...prev, ...filtersDefault }));
        } else {
            setUserFiltersDefault(null);
        }
    };
    const { loading: loadingGetDefault, refetch } = UsersEntity.GetUserFiltersPreferences(
        handleQueryCompletion,
        FiltersPreferencesReportsEnum.RCA_COMPLIANCE,
        currentUser?.user_id,
    );

    const handleSetFiltersDefault = async (filters: IRCAAPComplianceFiltersSelected) => {
        try {
            const validFilters = filterObjectProperties(filters, valiRCAAPComplianceFiltersDefaultFields);
            await upsertOperation({
                variables: {
                    userPreference: {
                        user_id: currentUser?.user_id,
                        report_id: FiltersPreferencesReportsEnum.RCA_COMPLIANCE,
                        filter_selection: JSON.stringify(validFilters),
                    },
                },
            });
            await refetch();
            setSuccessMessage(t("locations.mill_dashboards.filters_saved"));
        } catch (error) {
            console.error(error);
            setErrorMessage(t("locations.mill_dashboards.filters_error"));
        }
    };

    const handleRemoveDefaultFilters = async () => {
        try {
            await removeOperation({
                variables: {
                    userId: currentUser?.user_id,
                    reportId: FiltersPreferencesReportsEnum.RCA_COMPLIANCE,
                },
            });
            setUserFiltersDefault(null);
            setSuccessMessage(t("locations.mill_dashboards.filters_removed"));
        } catch (error) {
            console.error(error);
            setErrorMessage(t("locations.mill_dashboards.filters_error"));
        }
    };

    const handleSetFilters = (value: number) => {
        setFilters((prev) => ({ ...prev, periodOption: value as number }));
    };

    const handleSetTypeFilter = (value: string) => {
        setFilters((prev) => ({ ...prev, typeOption: value as string }));
    };

    const handleSetComplianceType = (value: string) => {
        setFilters((prev) => ({ ...prev, complianceType: value as string }));
    };

    const handleSetMeetingTypes = (value: (number | string)[], total?: number) => {
        setFilters((prev) => ({ ...prev, meetingTypeIds: value, ...(total ? { totalMeetingTypes: total } : {}) }));
    };

    const isFiltersDefault = useMemo(() => {
        if (userFiltersDefault) {
            return objectComparator(userFiltersDefault, filters, valiRCAAPComplianceFiltersDefaultFields);
        }

        return false;
    }, [userFiltersDefault, filters]);

    const providerValue = {
        userFiltersDefault,
        filters,
        handleSetFilters,
        handleSetTypeFilter,
        handleSetComplianceType,
        handleSetMeetingTypes,
        handleSetFiltersDefault,
        handleRemoveDefaultFilters,
        loadingDefaultOperations: loadingGetDefault || loadingRemoveDefault || loadingUpsertDefault,
        isFiltersDefault,
    };

    return (
        <RCAAPComplianceContext.Provider value={providerValue}>
            <Snackbar
                open={!!successMessage || !!errorMessage}
                autoHideDuration={2000}
                onClose={() => {
                    setErrorMessage("");
                    setSuccessMessage("");
                }}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
            >
                <Alert severity={successMessage ? "success" : "error"} sx={{ width: "100%" }}>
                    <span style={{ whiteSpace: "pre-line" }}>{successMessage || errorMessage}</span>
                </Alert>
            </Snackbar>
            {children}
        </RCAAPComplianceContext.Provider>
    );
};
